import { ChangeDetectionStrategy, Component, ViewChild, Injector } from "@angular/core";
import { MatTabGroup } from '@angular/material/tabs';
import { ComponentStore } from "@ngrx/component-store";
import { Store } from "@ngrx/store";
import {
  ClientSideTenantPermissionService,
  ClientSidePermissionRoleLinksService,
  Pagination,
  TenantRoleService,
  app_mst_role_RowClass,
  bl_applet_client_side_perm_dfn_RowClass,
} from "blg-akaun-ts-lib";
import { SubSink } from "subsink2";
import { of } from "rxjs";
import { filter, map, switchMap } from 'rxjs/operators';
import { pageFiltering, pageSorting, serverSideClear } from '../../../../../../../listing.utils';
import { PermissionFacade } from "../../../../../../../modules/permission/facades/permission.facade";
import { ViewColumnComponent } from "../../../../../../../view-column.component";
import { AppConfig } from "../../../../../../../visa";
import { ClientSidePermissionStates } from "../../../../../../permission/client-side-permissions-controller/states";
import { RolePermActions } from '../../../../../../../modules/permission/role-permissions-controller/actions';
import { RolePermSelectors } from '../../../../../../../modules/permission/role-permissions-controller/selectors';
import { PermissionStates } from '../../../../../permission-controller/root';
import { AgGridAngular } from 'ag-grid-angular';
import { PaginationComponent } from '../../../../../../../utilities/pagination/pagination.component';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  selectedIndex: number;
  deactivateAdd: boolean;
  deactivateList: boolean;
}

@Component({
  selector: "app-add",
  templateUrl: "./add.component.html",
  styleUrls: ["./add.component.css"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore],
})
export class AddRoleClientSidePermissionLinkComponent extends ViewColumnComponent {

  @ViewChild(PaginationComponent, {static: false})
  private paginationComponent: PaginationComponent;
  @ViewChild(MatTabGroup, {static: true}) matTab: MatTabGroup;
  @ViewChild('agGrid', {static: false}) aggrid!: AgGridAngular;

  protected readonly index = 6;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(
    (state) => state.localState.deactivateReturn
  );
  readonly selectedIndex$ = this.componentStore.select(state => (state.localState.selectedIndex));
  readonly deactivateAdd$ = this.componentStore.select(state => state.localState.deactivateAdd);
  readonly deactivateList$ = this.componentStore.select(state => (state.localState.deactivateList));

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;
  masterVisa = AppConfig.masterVisa;

  // keep track of form being enabled/disabled
  formDisabled: boolean = true;

  // no leakage of subscribe calls
  private subSink = new SubSink();

  // fields that require options
  newRoleArr: app_mst_role_RowClass[] = [];
  roleArr: app_mst_role_RowClass[] = [];
  newClientPermArr: bl_applet_client_side_perm_dfn_RowClass[] = [];
  clientPermArr: bl_applet_client_side_perm_dfn_RowClass[] = [];

  // search model
  searchValue: any;

  // perm templates
  permTemplateArr = [];
  newPermTemplateArr = [];

  // client side perms that are already added
  addedEntities = new Map<string, any>();
  // currently selected entities
  selectedEntities = new Map<string, any>();

  appletGuid: string = sessionStorage.getItem("appletGuid");
  roleGuid: string;

  // grid variables
  gridApi: any;
  gridColumnApi: any;
  rowData: any[] = [];
  gridOptions = {
    serverSideStoreType: 'partial',
    rowModelType: 'serverSide',
    defaultColDef: {
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        floatingFilterComponentParams: {suppressFilterButton: true},
        minWidth: 200,
        flex: 2,
        sortable: true,
        resizable: true,
        wrapText: true,
        autoHeight: true,
        suppressCsvExport: true
    },
    pagination: false,
    paginationPageSize: 999,
    animateRows: true,
    sideBar: 'columns'
};;
  columnsDefs;
  rowSelection = 'multiple';

  //translation variable
  title: string;
  code_text: string;
  name_text: string;
  add_button: string;
  back_button: string;
  select_all_text: string;

  constructor(
    private tenantClientSidePermissionService: ClientSideTenantPermissionService,
    private clientSidePermissionRoleLinksService: ClientSidePermissionRoleLinksService,
    private tenantRoleService: TenantRoleService,
    //private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private store: Store<ClientSidePermissionStates>,
    private readonly componentStore: ComponentStore<{ localState: LocalState }>,
    private permissionStore: Store<PermissionStates>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.add_client_side_permission;
        this.code_text = res.code;
        this.name_text = res.name;
        this.add_button = res.add_button_caps;
        this.back_button = res.back_button;
        this.select_all_text = res.select_all;
      });
    } else {
      this.title = 'Add Client Side Permission';
      this.code_text = 'Code';
      this.name_text = 'Name';
      this.add_button = 'ADD';
      this.back_button = 'Back';
      this.select_all_text = 'Select All';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState({localState: a});
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    // define column definitions
    this.columnsDefs = [
      {
        headerName: '',
        field: 'bl_applet_client_side_perm_dfn.guid',
        valueFormatter: params => '',
        checkboxSelection: true,
        cellStyle: params => {
          return this.addedEntities.has(params.value) ?
          {'pointer-events': 'none', textAlign: 'left'} : {textAlign: 'left'}
        },
        floatingFilter: false,
        sortable: false,
        minWidth: 0
      },
      {
        headerName: this.code_text,
        field: 'bl_applet_client_side_perm_dfn.perm_code',
        cellStyle: {textAlign: 'left'}
      },
      {
        headerName: this.name_text,
        field: 'bl_applet_client_side_perm_dfn.perm_name',
        cellStyle: {textAlign: 'left'}
      },
    ];

    this.subSink.sink = this.permissionStore.select(RolePermSelectors.selectGuid)
    .subscribe(x => {
      if(x) {
        this.roleGuid = x;
      }

      // get all current permission sets in role
      this.subSink.sink = this.getClientSidePermsInRole().subscribe(x => {
        x.forEach(a => {
          //console.log(a);
          // add permission set guids to addedEntities
          this.addedEntities.set(a.bl_applet_client_side_perm_to_role_link.client_perm_guid.toString(), a.bl_applet_client_side_perm_to_role_link);
        })
      })
    });
  }

  /**
   * Returns an observable containing all the client side permissions that are assigned to this
   * role.
   *
   * @returns an observable containing all the client side permissions that are assigned to this role
   */
  getClientSidePermsInRole() {
    const criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'role_guid', operator: '=', value: this.roleGuid},
      {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    ];
    const pagination = new Pagination(null, null, criteria);

    return this.clientSidePermissionRoleLinksService.getByCriteria(pagination, this.apiVisa)
      .pipe(
        map(x => x.data)
      )
  }

  /**
   * Function called when AG grid is initialised.
   *
   * @param params : grid parameters
   */
  onGridReady(params) {
    this.gridApi = params.api;
    this.gridApi.closeToolPanel();
    const datasource = this.getDatasource();
    this.gridApi.setServerSideDatasource(datasource);

    // autosize columns
    const allColIds = params.columnApi.getAllColumns().map(x => x.colId);
    params.columnApi.autoSizeColumns(allColIds);

    this.subSink.sink = this.permissionStore.select(RolePermSelectors.selectUpdateClientSideListing)
      .subscribe(x => {
        if (x) {
          // get all current client side permissions in role
          this.subSink.sink = this.getClientSidePermsInRole().subscribe(x => {
            this.addedEntities.clear();
            // add client side permissions
            x.forEach(a => {
              this.addedEntities.set(a.bl_applet_client_side_perm_to_role_link.client_perm_guid.toString(), a.bl_applet_client_side_perm_to_role_link);
            });
            this.gridApi.refreshServerSideStore(
              { purge: true }
            );
            this.store.dispatch(RolePermActions.setUpdateAllClientSideListing({update: false}));
          })
        }
      })
  }

  /**
   * Sets the datasource on the AG grid to response data returned from the
   * getByCriteria API.
   *
   * @param conditionalCriteria : any custom criteria to search for in the API
   * @returns datasource for AG grid
   */
  getDatasource(conditionalCriteria?) {
    let offset = 0;
    let limit = this.paginationComponent.rowPerPage;

    let criteria = [
      {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
      {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    ];
    if (conditionalCriteria) {
      criteria = criteria.concat(conditionalCriteria);
    }

    const sortCriteria = [
      { columnName: 'applet_guid', value: this.appletGuid },
      { columnName: 'orderBy', value: 'updated_date' },
      { columnName: 'order', value: 'DESC' }
    ];

    return {
      getRows: grid => {
        const sortModel = grid.request.sortModel;
        const filterModel = grid.request.filterModel;
        const sortOn = pageSorting(sortModel);
        const filter = pageFiltering(filterModel);

        // only change offset and limit if no filterModel or sortModel is present
        if (Object.keys(filterModel).length == 0 && sortModel.length == 0) {
          offset = grid.request.startRow;
          limit = grid.request.endRow - offset;
        } else {
          offset = 0;
          limit = 999999;
        }

        const pagination = new Pagination(offset, limit, criteria, sortCriteria);

        this.subSink.sink = this.tenantClientSidePermissionService.getByCriteria(pagination, this.apiVisa)
          .pipe()
          .subscribe(a => {
            // put in data to the grid
            const data = sortOn(a.data).filter(entity => filter.by(entity));
            const totalRecords = filter.isFiltering ? a.totalRecords :  data.length;

            // force pagination to go to first page if filter is applied
            // if (this.paginationComponent.currentPage > this.paginationComponent.totalPage.value) { this.paginationComponent.firstPage() }
            grid.successCallback(data, totalRecords);
            this.setSelectedNodes();
          }, err => {
            grid.failCallback();
          })
      }
    }
  }

  /**
   * Add client side permissions to the role.
   */
  onAdd() {
    const guids = [...this.selectedEntities.keys()];

    // clear selected
    this.selectedEntities.clear();
    // dispatch ngrx action to add client side permissions to role
    this.store.dispatch(RolePermActions.addClientSidePermsToRoleInit({clientGuids: guids, appletGuid: this.appletGuid, roleGuid: this.roleGuid}));
    // set form to disable again
    this.formDisabled = true;

    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  };

  /**
   * This function is to be called every time the grid refreshes.
   * Set nodes that are already in the role or previously currently
   * selected to selected.
   *
   */
  setSelectedNodes() {
    this.gridApi.forEachNode(node => {
      if (node.data) {
        const guid = node.data.bl_applet_client_side_perm_dfn.guid;
        const inRole = this.addedEntities.has(guid);
        const isCurrentlySelected = this.selectedEntities.has(guid);
        if (inRole || isCurrentlySelected) {
          node.setSelected(true);
        } else {
          node.setSelected(false);
        }
      }
    })
  }

  /**
   * This function is called when a row is either selected or deselected.
   * Updated the selectedEntities map.
   */
  onRowSelected(event) {
    const selectedGuid = (event.node) ? event.node.data.bl_applet_client_side_perm_dfn.guid : null;
    const isSelectionEvent = event.node.isSelected();
    const inRole = this.addedEntities.has(selectedGuid);
    const isCurrentlySelected = this.selectedEntities.has(selectedGuid);

    if (!isSelectionEvent && isCurrentlySelected && !inRole) {
      // delete from selected entities if node is not already in the role,
      // it is a deselection event and the node is in the currently selected entities
      this.selectedEntities.delete(selectedGuid)
    } else if (isSelectionEvent && !inRole && !isCurrentlySelected) {
      // add node to the currently selected entities if it is a selection event,
      // the node is not currently already in the role and the node is
      // not in the selected entities map already
      console.log("SELECTED GUID", selectedGuid);
      this.selectedEntities.set(selectedGuid, event.node.data)
    }

    if (this.selectedEntities.size > 0) {
      this.formDisabled = false;
    } else {
      this.formDisabled = true;
    }
  }

  /**
   * This function is called to select all items in the grid.
   */
  onSelectAll(event, isChecked: boolean): void {
    if (!this.gridApi) {
      console.error("Grid API not initialized");
      return;
    }

    // Construct filter and sort criteria
    const filterModel = this.gridApi.getFilterModel();
    // const sortModel = this.gridApi.getSortModel();

    // Get data that matches the filter from the server
    const criteria = [
      { columnName: 'applet_guid', operator: '=', value: this.appletGuid },
      ...this.constructFilterCriteria(filterModel), // dynamically generates criteria from filter model
    ];

    // let criteria = [
    //   {columnName: 'calcTotalRecords', operator: '=', value: 'true'},
    //   {columnName: 'applet_guid', operator: '=', value: this.appletGuid}
    // ];

    const sortCriteria = [
      { columnName: 'applet_guid', value: this.appletGuid },
      { columnName: 'orderBy', value: 'updated_date' },
      { columnName: 'order', value: 'DESC' }
    ];

    const pagination = new Pagination(0, null, criteria, sortCriteria);

    this.tenantClientSidePermissionService.getByCriteria(pagination, this.apiVisa)
      .subscribe((response) => {

        const filterModel = this.gridApi.getFilterModel();
        console.log("FILTER MODEL", filterModel);
        const roleFilterTextCode = filterModel['bl_applet_client_side_perm_dfn.perm_code']?.filter;
        const roleFilterTextName = filterModel['bl_applet_client_side_perm_dfn.perm_name']?.filter;
        console.log("Role filter text:", roleFilterTextCode);
        console.log("Role filter text:", roleFilterTextName);
        let filteredData;

        if(roleFilterTextCode) {
          filteredData = response.data.filter((row) => {
            return row.bl_applet_client_side_perm_dfn.perm_code.toUpperCase().includes(roleFilterTextCode.toUpperCase());
          });
        }

        if(roleFilterTextName) {
          filteredData = response.data.filter((row) => {
            return row.bl_applet_client_side_perm_dfn.perm_name.toUpperCase().includes(roleFilterTextName.toUpperCase());
          });
        }

        if (roleFilterTextCode || roleFilterTextName) {
          console.log("FILTERED DATA", filteredData);
          filteredData.forEach((row) => {
            const guid = row.bl_applet_client_side_perm_dfn.guid;
            console.log("PRINTING GUID", guid);
            const shouldSelect = isChecked && !this.addedEntities.has(guid?.toString());

            // Add to selectedEntities if it should be selected
            if (shouldSelect) {
              this.selectedEntities.set(guid?.toString(), row);
            }
          });

        } else {
          response.data.forEach((row) => {
            const guid = row.bl_applet_client_side_perm_dfn.guid;
            console.log("PRINTING GUID", guid);
            const shouldSelect = isChecked && !this.addedEntities.has(guid?.toString());

            // Add to selectedEntities if it should be selected
            if (shouldSelect) {
              this.selectedEntities.set(guid?.toString(), row);
            }
          });
        }

      // Refresh the grid to reflect changes
      // Use the grid API to select rows directly
      this.gridApi.forEachNode((node) => {
        const guid = node.data.bl_applet_client_side_perm_dfn.guid;
        if (this.selectedEntities.has(guid?.toString())) {
          node.setSelected(isChecked);
        }
      });
        this.formDisabled = this.selectedEntities.size === 0;
      });
  }

  /**
   * Utility function to generate filter criteria from ag-Grid's filter model.
   */
  private constructFilterCriteria(filterModel: any): any[] {
    const criteria = [];
    for (const columnKey in filterModel) {
      const filter = filterModel[columnKey];
      if (filter.filter) {
        criteria.push({ columnName: columnKey, operator: 'LIKE', value: `%${filter.filter}%` });
      }
    }
    return criteria;
  }

  /**
   * This function is called every time the user searches something in the
   * search bar.
   *
   * @param e : the search model
   */
  /**
   * Runs the search bar's keyword against the listing's own endpoint.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_applet_client_side_perm_dfn hdr
   *   WHERE (hdr.perm_code ILIKE '%x%' OR hdr.perm_name ILIKE '%x%')
   *     AND hdr.status != 'DELETED'
   *
   * GET applet client-side-permissions/applet-admin-ep/query ->
   * TenantClientSidePermissionUow.generateWhereClauseByCriteria applies
   * `search_word` as exactly `perm_code ILIKE ... OR perm_name ILIKE ...` and
   * defaults to `status != 'DELETED'`, so this is a like-for-like replacement.
   * The `applet_guid` scoping stays in this component's default criteria, which
   * getDatasource concatenates with what is passed in.
   */
  onSearch() {
    const datasource = this.getDatasource(this.buildSearchCriteria());
    this.gridApi.setServerSideDatasource(datasource);
  }

  /**
   * Criteria for the current keyword, or undefined when the box is empty so the
   * listing falls back to its default criteria.
   */
  buildSearchCriteria() {
    const keyword = (this.searchValue || '').toString().trim();
    if (!keyword) {
      return undefined;
    }
    return [
      { columnName: 'search_word', operator: '=', value: keyword }
    ];
  }

  /**
     * Return to the previous drawer/view column.
     */
  onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateReturn: false,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
  ngOnDestroy() {
    this.viewColFacade.updateInstance(this.index, {
      ...this.localState,
    });
    this.subSink.unsubscribe();
  }
}
